import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Lock } from "lucide-react";

const DISMISS_KEY = "reviewboost_2fa_banner_dismissed";

export function TwoFABanner() {
  const { role, isFounder } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(true);
  const [mfaEnrolled, setMfaEnrolled] = useState<boolean | null>(null);

  useEffect(() => {
    try {
      setDismissed(window.localStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.mfa.listFactors();
      if (cancelled) return;
      const hasTotp = (data?.totp?.length ?? 0) > 0;
      setMfaEnrolled(hasTotp);
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const isAdmin = role === "admin" || isFounder;
  if (!isAdmin || dismissed || mfaEnrolled === true) return null;
  if (mfaEnrolled === null) return null;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-amber-800 dark:text-amber-200 text-sm">
      <span className="flex items-center gap-2">
        <Lock className="h-4 w-4 shrink-0" />
        Strengthen your account security — enable 2FA in Settings → Security
      </span>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to="/settings"
          className="font-medium text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline"
        >
          Enable 2FA
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-1 rounded hover:bg-amber-500/20 text-foreground"
          aria-label="Dismiss"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
        </button>
      </div>
    </div>
  );
}
